'use client';

// Cookiebot is the consent authority for this site. It renders the banner, it
// records the visitor's choice, and it is what the client's Cookiebot scan
// verifies against — so nothing here stores a consent decision of its own.
// This module is a thin, typed reader/writer over `window.Cookiebot`.
//
// Two ways a script gets gated:
//   1. Plain <script> tags in the HTML carry `type="text/plain"` plus
//      `data-cookieconsent="statistics"`. Cookiebot's manual blocking mode
//      flips them to real scripts once consent is given. Nothing on the site
//      needs this today, but it is the shape any future third-party tag takes.
//   2. Anything loaded by our own code (PostHog via npm, GA via
//      @next/third-parties) has no such tag to mark, so it is gated in React
//      instead — see `useCookieConsent` below.

export type ConsentCategory =
  | 'necessary'
  | 'preferences'
  | 'statistics'
  | 'marketing';

// The names the rest of the app already uses, mapped onto Cookiebot's:
// functional → preferences, analytics → statistics, marketing → marketing.
export interface CookiePreferences {
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

interface CookiebotConsent {
  necessary: boolean;
  preferences: boolean;
  statistics: boolean;
  marketing: boolean;
}

interface CookiebotApi {
  consent: CookiebotConsent;
  consented: boolean;
  declined: boolean;
  hasResponse: boolean;
  /** Reopens the banner so the visitor can change their answer. */
  renew: () => void;
  /** Revokes consent entirely. */
  withdraw: () => void;
  /** Writes a per-category choice without showing the banner. */
  submitCustomConsent: (
    preferences: boolean,
    statistics: boolean,
    marketing: boolean
  ) => void;
}

declare global {
  interface Window {
    Cookiebot?: CookiebotApi;
  }
}

// Cookiebot fires these on `window` as the visitor's choice loads or changes.
const CONSENT_EVENTS = [
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
] as const;

export const NO_CONSENT: CookiePreferences = {
  analytics: false,
  marketing: false,
  functional: false,
};

function cookiebot(): CookiebotApi | undefined {
  return typeof window === 'undefined' ? undefined : window.Cookiebot;
}

/**
 * The visitor's current choice. Everything is false until Cookiebot has loaded
 * and the visitor has answered — deny-by-default is the whole point, and it
 * also keeps SSR and the first client render in agreement.
 */
export function getCookiePreferences(): CookiePreferences {
  const cb = cookiebot();
  if (!cb?.hasResponse) return NO_CONSENT;

  return {
    functional: cb.consent.preferences,
    analytics: cb.consent.statistics,
    marketing: cb.consent.marketing,
  };
}

export function hasConsent(category: ConsentCategory): boolean {
  const cb = cookiebot();
  if (category === 'necessary') return true;
  return Boolean(cb?.hasResponse && cb.consent[category]);
}

/** True once the visitor has accepted or declined; false while the banner waits. */
export function hasRespondedToBanner(): boolean {
  return Boolean(cookiebot()?.hasResponse);
}

/**
 * Writes a per-category choice straight to Cookiebot. Used by the privacy
 * settings page, where a single toggle changes one category and leaves the
 * others as the banner left them.
 */
export function setCookiePreferences(preferences: CookiePreferences): void {
  cookiebot()?.submitCustomConsent(
    preferences.functional,
    preferences.analytics,
    preferences.marketing
  );
}

/** Reopens the Cookiebot banner. */
export function renewConsent(): void {
  cookiebot()?.renew();
}

/** Revokes consent entirely. */
export function withdrawConsent(): void {
  cookiebot()?.withdraw();
}

/**
 * Calls `listener` whenever the visitor's choice loads or changes. Returns an
 * unsubscribe function.
 */
export function onConsentChange(
  listener: (preferences: CookiePreferences) => void
): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = () => listener(getCookiePreferences());
  CONSENT_EVENTS.forEach((event) => window.addEventListener(event, handler));

  return () => {
    CONSENT_EVENTS.forEach((event) =>
      window.removeEventListener(event, handler)
    );
  };
}
